import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const rawApiUrl =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:8003';

const API_URL = rawApiUrl.endsWith('/api/v1')
  ? rawApiUrl
  : `${rawApiUrl.replace(/\/$/, '')}/api/v1`;

const PermissionsContext = createContext(null);

const EMPTY_STATE = {
  permissions: [],
  menuItems: [],
  menuPermissions: {},
  role: null,
  systemRole: null,
  team: null,
  isSuperadmin: false,
  isSuperviewer: false,
  loading: true,
};

/**
 * Provider that fetches the user's permissions ONCE per session and shares
 * them with every consumer. Previously each `usePermissions()` call mounted
 * its own effect and issued an XHR — multiplied across Layout, ProtectedRoute
 * and PermissionGate that meant 4-8 redundant requests per navigation.
 */
export const PermissionsProvider = ({ children }) => {
  const [state, setState] = useState(EMPTY_STATE);
  // Timestamp of the last fetch attempt — throttles the focus/visibility
  // re-fetches so rapid tab switching doesn't hammer the endpoint.
  const lastFetchRef = useRef(0);

  const applySuccess = useCallback((data) => {
    setState({
      permissions: data.permissions || [],
      menuItems: data.menu_visibility || [],
      menuPermissions: data.menu_permissions || {},
      role: data.role,
      systemRole: data.system_role || data.role || null,
      team: data.team || null,
      isSuperadmin: Boolean(data.is_superadmin),
      isSuperviewer: Boolean(data.is_superviewer),
      loading: false,
    });

    localStorage.setItem('permissions', JSON.stringify(data.permissions || []));
    localStorage.setItem('menu_items', JSON.stringify(data.menu_visibility || []));
    localStorage.setItem('menu_permissions', JSON.stringify(data.menu_permissions || {}));
    localStorage.setItem('effective_role', data.role || '');
    localStorage.setItem('team', data.team || '');
    localStorage.setItem('is_superadmin', JSON.stringify(Boolean(data.is_superadmin)));
    localStorage.setItem('is_superviewer', JSON.stringify(Boolean(data.is_superviewer)));
  }, []);

  // `silent` re-fetches (focus / visibility / cross-tab token change) refresh
  // permissions in the background WITHOUT flipping `loading` back to true, so the
  // sidebar never flickers, and they leave the current state untouched on error
  // rather than falling back. The initial mount fetch is non-silent.
  const fetchPermissions = useCallback(async ({ silent = false } = {}) => {
    lastFetchRef.current = Date.now();
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setState({ ...EMPTY_STATE, loading: false });
        return;
      }

      const response = await axios.get(`${API_URL}/roles/my-permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      applySuccess(response.data);
    } catch (error) {
      if (silent) return; // keep whatever we already have on a background refresh

      // Fallback: try localStorage as last resort (offline / backend hiccup)
      const storedPermissions = localStorage.getItem('permissions');
      const storedMenuItems = localStorage.getItem('menu_items');
      const storedMenuPermissions = localStorage.getItem('menu_permissions');
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      if (storedPermissions && storedMenuItems) {
        setState({
          permissions: JSON.parse(storedPermissions),
          menuItems: JSON.parse(storedMenuItems),
          menuPermissions: storedMenuPermissions ? JSON.parse(storedMenuPermissions) : {},
          role: localStorage.getItem('effective_role') || user.team || user.role || null,
          systemRole: user.role || null,
          team: localStorage.getItem('team') || user.team || null,
          isSuperadmin: JSON.parse(localStorage.getItem('is_superadmin') || 'false'),
          isSuperviewer: JSON.parse(localStorage.getItem('is_superviewer') || 'false'),
          loading: false,
        });
      } else {
        setState({ ...EMPTY_STATE, loading: false });
      }
    }
  }, [applySuccess]);

  useEffect(() => {
    fetchPermissions();

    // Re-fetch in the background when the user returns to the tab / the window
    // regains focus, so permission & menu changes made in the Roles matrix (or
    // by an admin) reflect WITHOUT a manual hard refresh. Throttled so rapid tab
    // switches don't spam the endpoint.
    const THROTTLE_MS = 4000;
    const maybeRefetch = () => {
      if (Date.now() - lastFetchRef.current < THROTTLE_MS) return;
      fetchPermissions({ silent: true });
    };
    const onFocus = () => maybeRefetch();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeRefetch();
    };
    // Cross-tab token swap (login / logout / impersonation in another tab) —
    // re-fetch immediately and non-silently so loading reflects the new identity.
    const onStorage = (event) => {
      if (event.key === 'token') fetchPermissions();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
    };
  }, [fetchPermissions]);

  return (
    <PermissionsContext.Provider value={state}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * Hook to read permissions. Must be used inside a <PermissionsProvider>.
 * Returns the same shape as the previous per-mount-fetch version so call
 * sites don't need to change.
 */
export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);

  // Defensive: if used outside the provider during a transient render,
  // return a safe empty shape instead of throwing.
  const safe = ctx || EMPTY_STATE;
  const { permissions, menuItems, menuPermissions, role, systemRole, team, isSuperadmin, isSuperviewer, loading } = safe;

  // Defense in depth: superadmin bypasses every check, even if isSuperviewer
  // didn't propagate (e.g. older session where the backend hadn't yet started
  // setting it from is_superadmin). The per-user superadmin flag is the most
  // reliable "see everything" signal because it's stamped on the user row.
  const _bypass = isSuperviewer || isSuperadmin;

  const hasPermission = useCallback(
    (permission) => _bypass || permissions.includes(permission),
    [permissions, _bypass]
  );

  const hasAnyPermission = useCallback(
    (permissionList) => _bypass || permissionList.some((p) => permissions.includes(p)),
    [permissions, _bypass]
  );

  const hasAllPermissions = useCallback(
    (permissionList) => _bypass || permissionList.every((p) => permissions.includes(p)),
    [permissions, _bypass]
  );

  const isMenuVisible = useCallback(
    (menuItem) => _bypass || menuItems.includes(menuItem),
    [menuItems, _bypass]
  );

  const hasMenuPermission = useCallback(
    (menuKey, action = 'view') => {
      if (_bypass) return true;
      const menu = menuPermissions?.[menuKey];
      if (!menu) return false;

      const actionMap = {
        view: 'can_view',
        read: 'can_read',
        create: 'can_create',
        update: 'can_update',
        delete: 'can_delete',
      };

      const field = actionMap[action] || actionMap.view;
      return Boolean(menu[field]);
    },
    [menuPermissions, _bypass]
  );

  const isAdmin = useCallback(() => systemRole === 'admin', [systemRole]);

  return useMemo(
    () => ({
      permissions,
      menuItems,
      menuPermissions,
      role,
      systemRole,
      team,
      isSuperadmin,
      isSuperviewer,
      loading,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      isMenuVisible,
      hasMenuPermission,
      isAdmin,
    }),
    [permissions, menuItems, menuPermissions, role, systemRole, team, isSuperadmin, isSuperviewer, loading, hasPermission, hasAnyPermission, hasAllPermissions, isMenuVisible, hasMenuPermission, isAdmin]
  );
};

/**
 * Conditional render helper — same signature as before.
 */
export const PermissionGate = ({ permission, children, fallback = null }) => {
  const { hasPermission, hasAnyPermission } = usePermissions();
  if (Array.isArray(permission)) {
    return hasAnyPermission(permission) ? children : fallback;
  }
  return hasPermission(permission) ? children : fallback;
};

export default usePermissions;
